import * as React from "react";
import { cn } from "@/lib/utils";

const Skeleton = React.memo(({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("skeleton", className)} {...props} />
));
Skeleton.displayName = "Skeleton";

type ListSkeletonProps = {
  rows?: number;
  className?: string;
};

const ListSkeleton = React.memo(({ rows = 4, className }: ListSkeletonProps) => (
  <div className={cn("space-y-3", className)}>
    {Array.from({ length: rows }).map((_, index) => (
      <div key={`list-skeleton-${index}`} className="glass-panel rounded-[var(--card-radius)] p-card">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-8 w-20 rounded-full" />
        </div>
      </div>
    ))}
  </div>
));
ListSkeleton.displayName = "ListSkeleton";

type TableSkeletonProps = {
  rows?: number;
  columns?: number;
  className?: string;
};

const TableSkeleton = React.memo(({ rows = 5, columns = 4, className }: TableSkeletonProps) => (
  <div className={cn("space-y-3", className)}>
    <div className="grid gap-3 rounded-lg border border-border/60 bg-card/70 p-4">
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={`table-header-${index}`} className="h-3 w-3/4" />
        ))}
      </div>
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={`table-row-${rowIndex}`}
            className="grid gap-4"
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton key={`table-cell-${rowIndex}-${colIndex}`} className="h-3 w-full" />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
));
TableSkeleton.displayName = "TableSkeleton";

type CardGridSkeletonProps = {
  cards?: number;
  className?: string;
};

const CardGridSkeleton = React.memo(({ cards = 6, className }: CardGridSkeletonProps) => (
  <div className={cn("grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4", className)}>
    {Array.from({ length: cards }).map((_, index) => (
      <div key={`card-skeleton-${index}`} className="rounded-xl border border-border/60 bg-card/70 p-4 space-y-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
        <div className="flex gap-2">
          <Skeleton className="h-7 w-20 rounded-full" />
          <Skeleton className="h-7 w-16 rounded-full" />
        </div>
      </div>
    ))}
  </div>
));
CardGridSkeleton.displayName = "CardGridSkeleton";

type DrawerSkeletonProps = {
  lines?: number;
  className?: string;
};

const DrawerSkeleton = React.memo(({ lines = 4, className }: DrawerSkeletonProps) => (
  <div className={cn("space-y-6", className)}>
    <div className="space-y-2">
      <Skeleton className="h-6 w-1/2" />
      <Skeleton className="h-4 w-2/3" />
    </div>
    <div className="space-y-3">
      {Array.from({ length: lines }).map((_, index) => (
        <div key={`drawer-line-${index}`} className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      ))}
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-9 w-24 rounded-md" />
      <Skeleton className="h-9 w-20 rounded-md" />
    </div>
  </div>
));
DrawerSkeleton.displayName = "DrawerSkeleton";

export { Skeleton, ListSkeleton, TableSkeleton, CardGridSkeleton, DrawerSkeleton };
